import React from "react";
import PhotoFrameCropping from "./PhotoFrameCropping";
import { connect } from "react-redux";
import {
  isCroppingSelector,
  contextSelector,
  selectedItemsSelector,
  zoomSelector,
  designDataSelector,
  elementPreviewsSelector
} from "state/ui/editorContext/editorContextSelectors";
import { getPageOffsetInCanvasbackground } from "views/components/Editor/utils";
import { update as updateEditorContextState } from "state/ui/editorContext/editorContextActions";
import { Logger } from "lib";
import EditorPhotoFrameOps from "views/components/Editor/editorOps/EditorPhotoFrameOps";
import { setInnerImageRotation } from "state/ui/editor/innerImageRotationUtils";

class PhotoFrameCroppingContainer extends React.Component {
  constructor(props) {
    super(props);

    this.startPhotoFrameCropMoving = this.startPhotoFrameCropMoving.bind(this);
    this.finishPhotoFrameCropMoving =
      this.finishPhotoFrameCropMoving.bind(this);
    this.handleInnerImageRotation = this.handleInnerImageRotation.bind(this);
    this.handleInnerImageRotationPreview =
      this.handleInnerImageRotationPreview.bind(this);
    this.pendingPreviewRotation = null;
    this.previewRafId = null;
  }

  componentWillUnmount() {
    if (this.previewRafId !== null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
  }

  // Preview ticks fire on every drag move (mouse / touch / slider). Without
  // coalescing, each tick clones selectedItems and dispatches updateContext
  // through redux — visibly janky on larger designs. Throttling to one
  // dispatch per animation frame caps the redux churn at the display rate
  // while keeping the visual rotation feedback smooth. The commit path
  // (mouseup) bypasses this and writes immediately so the final value is
  // never a frame stale.
  handleInnerImageRotationPreview(rotation) {
    this.pendingPreviewRotation = rotation;
    if (this.previewRafId !== null) return;
    this.previewRafId = requestAnimationFrame(() => {
      this.previewRafId = null;
      const next = this.pendingPreviewRotation;
      this.pendingPreviewRotation = null;
      if (next === null || next === undefined) return;
      this.handleInnerImageRotation(next);
    });
  }

  handleInnerImageRotation(rotation) {
    // Cancel any preview rAF still queued from the same drag — otherwise
    // the queued frame fires after this synchronous commit and dispatches
    // a redundant updateContext (idempotent today thanks to lastRotationRef
    // in InnerImageRotateHandle, but still costs a full selectedItems
    // clone + redux roundtrip per drag and would mask any future "snap to"
    // commit value that didn't match the last preview).
    if (this.previewRafId !== null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
    this.pendingPreviewRotation = null;

    // Preview and commit go through the same path because the photo-frame
    // crop flow is preview-only until the user clicks apply crop — at which
    // point applyVectorCrop spreads selectedItems[0].preview onto designData.
    const { selectedItems, designData, context } = this.props;
    const { selectedPhotoInFrameId } = context;

    if (!selectedItems[0] || !selectedPhotoInFrameId) return;

    const element = designData.getElement(selectedItems[0].itemId);
    const existingPreview = selectedItems[0].preview || {};
    const baseInstructions =
      existingPreview.imageInstructions || element.imageInstructions || [];

    // Pass the actual element (with the preview-merged instructions overlaid)
    // to setInnerImageRotation so its element.type drives the branch instead
    // of a synthetic { type: "vector" } literal — Stages 2/3 add non-vector
    // element types and must not silently fall through here.
    const { imageInstructions: updatedInstructions } = setInnerImageRotation(
      { ...element, imageInstructions: baseInstructions },
      selectedPhotoInFrameId,
      rotation
    );

    const updatedSelectedItems = selectedItems.map((item, index) =>
      index === 0
        ? {
            ...item,
            preview: {
              ...existingPreview,
              imageInstructions: updatedInstructions
            }
          }
        : item
    );

    this.props.updateContext({ selectedItems: updatedSelectedItems });
  }

  startPhotoFrameCropMoving() {
    Logger.info(
      "PhotoFrameCroppingContainer.startPhotoInFrameCropMoving called"
    );

    this.props.updateContext({
      isCropping: true
    });
  }

  finishPhotoFrameCropMoving({ domId, differenceFromInitialOffset }) {
    Logger.info(
      "PhotoFrameCroppingContainer.finishPhotoInFrameCropMoving called"
    );
    const { zoom, selectedItems, designData, updateContext } = this.props;

    const updatedContext = EditorPhotoFrameOps.finishPhotoFrameCropMoving({
      zoom,
      selectedItems,
      designData,
      domId,
      differenceFromInitialOffset
    });

    updateContext(updatedContext);
  }

  render() {
    const {
      canvasBackground,
      context: {
        isCroppingPhotoFrame,
        selectedPhotoInFrameId,
        vectorHtmlElement,
        photoFrameOffsetRect,
        placeholderDimensions
      },
      designData,
      selectedItems,
      zoom,
      isCropping
    } = this.props;

    if (!canvasBackground || !isCroppingPhotoFrame) {
      return null;
    }

    const { vectorElement, photoFrame } =
      EditorPhotoFrameOps.getVectorElementWithPreview({
        selectedItems,
        designData,
        selectedPhotoInFrameId
      });
    const pageId = selectedItems[0].pageId;
    const pageOffset = getPageOffsetInCanvasbackground(pageId);

    return (
      <PhotoFrameCropping
        {...this.props}
        vectorHtmlElement={vectorHtmlElement}
        photoFrameOffsetRect={photoFrameOffsetRect}
        placeholderDimensions={placeholderDimensions}
        startPhotoFrameCropMoving={this.startPhotoFrameCropMoving}
        finishPhotoFrameCropMoving={this.finishPhotoFrameCropMoving}
        onInnerImageRotationPreview={this.handleInnerImageRotationPreview}
        onInnerImageRotationCommit={this.handleInnerImageRotation}
        photoFrame={photoFrame}
        vectorElement={vectorElement}
        pageOffset={pageOffset}
        pageId={pageId}
        zoom={zoom}
        canvasBackground={canvasBackground}
        isCropping={isCropping}
      />
    );
  }
}

const mapStateToProps = state => {
  return {
    isCropping: isCroppingSelector(state),
    context: contextSelector(state),
    selectedItems: selectedItemsSelector(state),
    zoom: zoomSelector(state),
    elementPreviews: elementPreviewsSelector(state),
    designData: designDataSelector(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateContext: args => dispatch(updateEditorContextState(args))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PhotoFrameCroppingContainer);
